import { LinkProps } from 'next/link';
import { ReactElement, useMemo } from 'react';
import CompassIcon from '@mui/icons-material/Explore';
import { useTranslation } from 'react-i18next';
import { useGetUserQuery } from 'src/components/User/GetUser.generated';
import { UserTypeEnum } from 'src/graphql/types.generated';
import { useAccountListId } from 'src/hooks/useAccountListId';
import { useDeveloperBypass } from './useDeveloperBypass';
import { useHrToolsNavItems } from './useHrToolsNavItems';
import { useReportNavItems } from './useReportNavItems';
import { useSettingsNavItems } from './useSettingsNavItems';
import { useToolsNavItems } from './useToolsNavItems';

interface SubItems {
  id?: string;
  title?: string;
  grantedAccess?: string[];
}
interface Item {
  id: string;
  href?: LinkProps['href'];
  icon?: string;
  items?: Item[];
  title: string;
  subtitle?: string;
  desc?: string;
  // search dialog specific
  showInSearchDialog?: boolean;
  searchIcon?: ReactElement;
  searchName?: string;
  // settings specific
  grantedAccess?: string[];
  subItems?: SubItems[];
  oauth?: boolean;
}

export interface NavPage {
  id?: string;
  title: string;
  href?: LinkProps['href'];
  pathname?: string;
  searchIcon?: ReactElement;
  items?: Item[];
  showInNav?: boolean;
  showInSearchDialog?: boolean;
  showInPanel?: boolean;
  isDropdown?: boolean;
  hideTab?: boolean;
}

export function useNavPages(coachingAccountCount: boolean, isSearch = false) {
  const accountListId = useAccountListId();
  const { t } = useTranslation();
  const { data } = useGetUserQuery();

  const userType = data?.user.userType;
  const developerBypass = useDeveloperBypass();
  const canSeeHrTools =
    userType === UserTypeEnum.UsStaff ||
    userType === UserTypeEnum.HybridStaff ||
    developerBypass;

  const reportItems = useReportNavItems();
  const toolsItems = useToolsNavItems();
  const settingsItems = useSettingsNavItems();
  const { items: hrToolsItems, loading: hrToolsLoading } = useHrToolsNavItems();

  const allNavPages = useMemo<NavPage[]>(() => {
    const navPages: NavPage[] = [
      {
        id: 'dashboard-page',
        title: t('Dashboard'),
        href: `/accountLists/${accountListId}`,
        pathname: '/accountLists/[accountListId]',
        showInNav: true,
        isDropdown: false,
      },
      {
        id: 'contacts-page',
        title: t('Contacts'),
        searchIcon: <CompassIcon />,
        href: `/accountLists/${accountListId}/contacts`,
        pathname: '/accountLists/[accountListId]/contacts/[[...contactId]]',
        showInNav: true,
        isDropdown: false,
        showInSearchDialog: true,
      },
      {
        id: 'tasks-page',
        title: t('Tasks'),
        searchIcon: <CompassIcon />,
        href: `/accountLists/${accountListId}/tasks`,
        pathname: '/accountLists/[accountListId]/tasks/[[...contactId]]',
        showInNav: true,
        isDropdown: false,
        showInSearchDialog: true,
      },
      {
        id: 'reports-page',
        title: t('Reports'),
        pathname: '/accountLists/[accountListId]/reports',
        items: reportItems.map((item) => ({
          ...item,
          href: `/accountLists/${accountListId}/reports/${item.id}`,
          searchIcon: <CompassIcon />,
          searchName: t(`Reports - {{ title }}`, { title: item.title }),
          showInSearchDialog: true,
        })),
        showInNav: true,
      },
      {
        id: 'hr-tools-page',
        title: t('HR Tools'),
        pathname: '/accountLists/[accountListId]/hrTools',
        items: hrToolsItems.map((item) => ({
          ...item,
          href: `/accountLists/${accountListId}/hrTools/${item.id}`,
          searchIcon: <CompassIcon />,
          searchName: t(`HR Tools - {{ title }}`, { title: item.title }),
          showInSearchDialog: true,
        })),
        showInNav: true,
        hideTab:
          (!!data && !canSeeHrTools) ||
          hrToolsLoading ||
          hrToolsItems.length === 0,
      },
      {
        id: 'mpdx-tools-page',
        title: t('MPDX Tools'),
        searchIcon: <CompassIcon />,
        href: `/accountLists/${accountListId}/tools`,
        pathname: '/accountLists/[accountListId]/tools',
        items: toolsItems.flatMap((toolsGroup) =>
          toolsGroup.items.map((tool) => ({
            ...tool,
            title: tool.tool,
            href: `/accountLists/${accountListId}/tools/${tool.url}`,
            searchIcon: <CompassIcon />,
            searchName: t(`MPDX Tools - {{ title }}`, { title: tool.tool }),
            showInSearchDialog: true,
          })),
        ),
        showInNav: true,
        showInSearchDialog: true,
      },
      {
        id: 'settings-page',
        title: t('Preferences'),
        searchIcon: <CompassIcon />,
        href: `/accountLists/${accountListId}/settings/preferences`,
        items: settingsItems.map((item) => ({
          id: item.id,
          title: item.title,
          subtitle: item.subTitle,
          href: `/accountLists/${accountListId}/settings/${item.id}`,
          searchIcon: <CompassIcon />,
          searchName:
            item.id === 'preferences'
              ? item.title
              : t(`Preferences - {{ title }}`, { title: item.title }),
          grantedAccess: item.grantedAccess,
          subItems: item.subItems,
          oauth: item.oauth,
          showInSearchDialog: true,
        })),
        pathname: '/accountLists/[accountListId]/settings/preferences',
        showInPanel: true,
      },
    ];

    if (coachingAccountCount || isSearch) {
      navPages.push({
        id: 'coaching-page',
        title: t('Coaching'),
        searchIcon: <CompassIcon />,
        href: `/accountLists/${accountListId}/coaching`,
        pathname: '/accountLists/[accountListId]/coaching',
        showInNav: true,
        showInSearchDialog: true,
      });
    }

    return navPages.filter((page) => !page.hideTab);
  }, [
    accountListId,
    coachingAccountCount,
    t,
    reportItems,
    toolsItems,
    settingsItems,
    hrToolsItems,
    hrToolsLoading,
    canSeeHrTools,
    data,
  ]);

  const navPages = useMemo(
    () => allNavPages.filter((page) => page.showInNav),
    [allNavPages],
  );

  const searchDialogPages = useMemo(() => {
    const pages: NavPage[] = [];

    for (const page of allNavPages) {
      // include search pages
      if (page.showInSearchDialog) {
        pages.push(page);
      }

      // get report sub items
      if (page.id === 'reports-page' && page.items) {
        page.items.forEach((item) => {
          pages.push({ ...item, title: item.searchName ?? item.title });
        });
      }

      // get request form sub items
      if (page.id === 'hr-tools-page' && page.items) {
        page.items.forEach((item) => {
          pages.push({ ...item, title: item.searchName ?? item.title });
        });
      }

      // get tool sub items
      if (page.id === 'mpdx-tools-page' && page.items) {
        if (page.items) {
          page.items.forEach((item) => {
            pages.push({ ...item, title: item.searchName ?? item.title });
          });
        }
      }

      // get settings sub items without granted access
      if (page.id === 'settings-page' && page.items) {
        page.items
          .filter((item) => !item.grantedAccess)
          .forEach((item) => {
            pages.push({ ...item, title: item.searchName ?? item.title });
          });
      }
    }

    return pages.map(({ id: _omit, ...rest }) => rest);
  }, [allNavPages]);

  const panelPages = useMemo(
    () =>
      allNavPages
        .filter((page) => page.showInPanel)
        .map((p) => {
          const name = p.title.split(' - ')[1];
          return { ...p, title: name ?? p.title };
        }),
    [allNavPages],
  );

  return { navPages, searchDialogPages, panelPages };
}
